import React, {
  CSSProperties,
  HTMLAttributes,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  ColumnDef as TanColumnDef,
  Row,
  SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames';
import { FixedSizeList as List } from 'react-window';

import styles from './Table.module.css';
import { useScrollBarWidth } from '../../helpers/useScrollBarWidth';
import { Icon } from '../icon/Icon';
import { Skeleton } from '../skeleton/Skeleton';
import { Spinner } from '../spinner/Spinner';

export type ColumnDef<TableModel> =
  | (TanColumnDef<TableModel, any> & {
      className?: string;
      style?: CSSProperties;
      visible?: never;
    })
  | (TanColumnDef<TableModel, any> & {
      className?: string;
      style?: CSSProperties;
      id: string;
      visible?: boolean;
    });

export type TableProps<TableModel> = {
  data: TableModel[];
  columns: ColumnDef<TableModel>[];
  small?: boolean;
  maxHeight?: number;
  pinFirstCol?: boolean;
  onRowClick?: (row: Row<TableModel>) => void;
  rowClassName?: string | ((row: Row<TableModel>) => string | undefined);
  rowStyle?:
    | CSSProperties
    | ((row: Row<TableModel>) => CSSProperties | undefined);
  loading?: boolean;
} & HTMLAttributes<HTMLTableElement>;

/**
 * @experiemental Component to display tabular data
 */
export const Table = <TableModel extends object>({
  data,
  columns,
  small,
  maxHeight,
  pinFirstCol = true,
  onRowClick,
  rowClassName,
  rowStyle,
  style,
  className,
  loading,
  ...tableProps
}: TableProps<TableModel>) => {
  const tableRef = useRef<HTMLTableElement>(null);
  const tableBodyRef = useRef<HTMLTableSectionElement>(null);
  const tableHeadRef = useRef<HTMLTableSectionElement>(null);
  const tableFootRef = useRef<HTMLTableSectionElement>(null);
  const scrollbarWidth = useScrollBarWidth();
  const [scrollXPos, setScrollXPos] = useState(0);
  const [scrollYPos, setScrollYPos] = useState(0);
  const [scrollYVisible, setScrollYVisible] = useState(false);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const hasFooter = columns.some((col) => col.footer);

  columns = useMemo(
    () =>
      columns.map((col) => ({
        ...col,
        enableSorting:
          col.enableSorting === undefined ? false : col.enableSorting,
      })),
    [columns],
  );

  const columnVisibility = useMemo(
    () =>
      columns.reduce((accumulator, col) => {
        return col.id
          ? {
              ...accumulator,
              [col.id]: col.visible ?? true,
            }
          : accumulator;
      }, {}),
    [columns],
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnVisibility: columnVisibility,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });
  const headers = table.getHeaderGroups();
  const rows = table.getRowModel().rows;
  const footers = table.getFooterGroups();

  // Track number of Columns
  useEffect(() => {
    tableRef?.current?.style.setProperty('--noCols', columns.length.toString());
  }, [tableRef, columns]);

  // Track scroll Left/Top for overlays
  useLayoutEffect(() => {
    const scrollChange = (ev: any) => {
      if (tableHeadRef.current && tableBodyRef.current) {
        setScrollXPos(ev.target.scrollLeft);
        setScrollYPos(ev.target.scrollTop);
        tableHeadRef.current.scrollLeft = ev.target.scrollLeft;
        tableBodyRef.current.scrollLeft = ev.target.scrollLeft;
      }
    };

    const tfoot = tableFootRef.current;
    const tbody = tableBodyRef.current;
    tfoot?.addEventListener('scroll', scrollChange);
    tbody?.addEventListener('scroll', scrollChange);

    return () => {
      tfoot?.removeEventListener('scroll', scrollChange);
      tbody?.removeEventListener('scroll', scrollChange);
    };
  }, [tableBodyRef, tableHeadRef, tableFootRef]);

  // check if vertical scroll is visible
  useLayoutEffect(() => {
    if (tableBodyRef?.current) {
      setScrollYVisible(
        tableBodyRef.current.scrollHeight > tableBodyRef.current.clientHeight,
      );
    }
  }, [tableBodyRef]);

  const renderHeader = () => {
    return (
      <thead
        ref={tableHeadRef}
        className={styles.thead}
        style={{
          paddingRight: scrollYVisible ? `${scrollbarWidth}px` : `0`,
        }}
      >
        {headers.map((headerGroup) => (
          <tr key={headerGroup.id} className={styles.tr}>
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                className={styles.th}
                style={{
                  width:
                    header.getSize() !== 150 ? header.getSize() : undefined,
                  minWidth:
                    header.getSize() !== 150 ? header.getSize() : undefined,
                  cursor: header.column.getCanSort() ? 'pointer' : undefined,
                }}
                onClick={header.column.getToggleSortingHandler()}
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                {header.column.getIsSorted() === 'asc' && (
                  <button
                    className={styles.sortButton}
                    type="button"
                    aria-label="Sort Descending"
                  >
                    <Icon icon="arrowUp" />
                  </button>
                )}
                {header.column.getIsSorted() === 'desc' && (
                  <button
                    className={styles.sortButton}
                    type="button"
                    aria-label="Remove Sort"
                  >
                    <Icon icon="arrowDown" />
                  </button>
                )}
                {header.column.getIsSorted() === false &&
                  header.column.getCanSort() && (
                    <button
                      className={styles.sortButton}
                      type="button"
                      aria-label="Sort Descending"
                    >
                      <Icon icon="sort" />
                    </button>
                  )}
              </th>
            ))}
          </tr>
        ))}
      </thead>
    );
  };

  const renderRow = (index: number, style: CSSProperties) => {
    const row = rows[index];
    delete style.width;
    return (
      <tr
        key={row.id}
        className={classNames(styles.tr, {
          [styles.lastRow]: !hasFooter && index === rows.length - 1,
        })}
        style={{
          cursor: onRowClick ? 'pointer' : undefined,
          ...style,
        }}
        onClick={() => onRowClick && onRowClick(row)}
      >
        {row.getVisibleCells().map((cell) => {
          const columnDef = cell.column.columnDef as ColumnDef<TableModel>;
          return (
            <td
              key={cell.id}
              className={classNames(
                styles.td,
                columnDef.className,
                rowClassName && typeof rowClassName === 'function'
                  ? rowClassName(row)
                  : rowClassName,
              )}
              style={{
                width:
                  cell.column.getSize() !== 150
                    ? cell.column.getSize()
                    : undefined,
                minWidth:
                  cell.column.getSize() !== 150
                    ? cell.column.getSize()
                    : undefined,
                ...columnDef.style,
                ...(rowStyle && typeof rowStyle === 'function'
                  ? rowStyle(row)
                  : rowStyle),
              }}
            >
              {loading ? (
                <Skeleton lastLineLength={70} />
              ) : (
                flexRender(cell.column.columnDef.cell, cell.getContext())
              )}
            </td>
          );
        })}
      </tr>
    );
  };

  const renderFooter = () => {
    return (
      <tfoot
        ref={tableFootRef}
        className={styles.tfoot}
        style={{
          paddingRight: scrollYVisible ? `${scrollbarWidth}px` : `0`,
        }}
      >
        {footers.map((footerGroup) => (
          <tr key={footerGroup.id} className={styles.tr}>
            {footerGroup.headers.map((header) => (
              <th key={header.id} className={styles.th}>
                {header.isPlaceholder ? null : loading ? (
                  <Skeleton lastLineLength={70} />
                ) : (
                  flexRender(
                    header.column.columnDef.footer,
                    header.getContext(),
                  )
                )}
              </th>
            ))}
          </tr>
        ))}
      </tfoot>
    );
  };

  return (
    <table
      ref={tableRef}
      className={classNames(
        styles.table,
        {
          [styles.small]: small,
          [styles.scrolledX]: scrollXPos > 0,
          [styles.scrolledY]: scrollYPos > 0,
          [styles.pinFirstCol]: pinFirstCol,
          [styles.loading]: loading,
          [styles.noData]: rows.length === 0,
        },
        className,
      )}
      style={style}
      aria-rowcount={rows.length}
      {...tableProps}
    >
      {renderHeader()}

      {rows.length > 300 && maxHeight ? (
        // Windowed Table rows
        <tbody
          className={classNames(styles.tbody, {
            [styles.scrollX]: !hasFooter,
          })}
          style={{ maxHeight: `${maxHeight}px` }}
        >
          <tr
            className={styles.verticalScrollOverlay}
            hidden
            aria-hidden="true"
          />

          <List
            height={maxHeight}
            itemCount={rows.length}
            itemSize={small ? 49 : 57}
            width="100%"
            overscanCount={5}
            outerRef={tableBodyRef}
          >
            {({ index, style }) => renderRow(index, style)}
          </List>
        </tbody>
      ) : (
        // Render all rows
        <tbody
          ref={tableBodyRef}
          className={classNames(styles.tbody, {
            [styles.scrollX]: !hasFooter,
          })}
          style={{ maxHeight: `${maxHeight}px` }}
        >
          <tr
            className={styles.verticalScrollOverlay}
            hidden
            aria-hidden="true"
          />

          {loading && rows.length === 0 ? (
            <tr className={styles.tr}>
              <td className={styles.td}>
                <Spinner />
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr className={classNames(styles.noData, styles.tr)}>
              <td className={styles.td}>{'  '}</td>
            </tr>
          ) : (
            rows.map((row, index) => renderRow(index, {}))
          )}
        </tbody>
      )}

      {hasFooter && renderFooter()}
    </table>
  );
};
